import React, { useState } from "react";
import { MessageSquare, Zap, GitBranch, Loader2, Sparkles, ArrowLeft } from "lucide-react";
import {
  useGetTriggersQuery,
  useGetActionsQuery,
  useGetConditionsQuery,
  useGetTriggerableWorkflowsQuery,
} from "@/services/WorkFlowBuilderApi";
import { WorkflowNodeType, WorkflowCreate } from "@/app/types/workflow";
import { AIWorkflowModal } from "./AIWorkflowModal";
import { Button } from "@/components/ui/Button";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { ChevronDown } from "lucide-react";

export const Sidebar = ({
  onCreateWorkflow,
  isCreating,
  isEditMode,
  onAIWorkflowGenerated,
}: {
  onCreateWorkflow: (status?: string) => void;
  isCreating: boolean;
  isEditMode: boolean;
  onAIWorkflowGenerated: (workflow: WorkflowCreate) => void;
}) => {
  const { data: triggers, isLoading: isLoadingTriggers } = useGetTriggersQuery();
  const { data: actions, isLoading: isLoadingActions } = useGetActionsQuery();
  const { data: conditions, isLoading: isLoadingConditions } = useGetConditionsQuery();
  const { data: triggerableWorkflows, isLoading: isLoadingTriggerableWorkflows } = useGetTriggerableWorkflowsQuery();
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const navigate = useNavigate();

  const onDragStart = (event: React.DragEvent, node: WorkflowNodeType) => {
    const nodeData = { ...node };

    // Inject workflow_id into config for triggerable workflow nodes
    if (node.node_type === "workflow" && (node as any).id) {
      nodeData.config = {
        ...nodeData.config,
        workflow_id: (node as any).id,
      };
    }

    event.dataTransfer.setData("application/reactflow", JSON.stringify(nodeData));
    event.dataTransfer.effectAllowed = "move";
  };

  const renderNodeList = (
    title: string,
    items: WorkflowNodeType[] | undefined,
    isLoading: boolean,
    icon: React.ReactNode,
    bgColor: string,
    borderColor: string,
    hoverColor: string,
    textColor: string
  ) => {
    return (
      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
          {title}
        </h3>
        {isLoading ? (
          <div className="flex justify-center p-2">
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          </div>
        ) : (
          items?.map((item, index) => (
            <div
              key={`${item.node_type}-${index}`}
              className={`flex items-center gap-2 p-3 ${bgColor} border ${borderColor} rounded-lg cursor-grab hover:${hoverColor} transition-colors`}
              onDragStart={(event) => onDragStart(event, item)}
              draggable
              title={item.description}
            >
              {icon}
              <div className="flex flex-col">
                <span className={`font-medium ${textColor} text-sm`}>
                  {item.label}
                </span>
                {item.node_type !== "workflow" && <span className="text-xs text-gray-500 line-clamp-1">
                  {item.description}
                </span>}
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <aside className="w-64 bg-white border-r border-gray-200 flex flex-col h-full">
      {/* Fixed Header */}
      <div className="px-4 pb-4 border-b border-gray-200 flex flex-col gap-4">
        <div className="flex align-center">
          <Button
            size="icon"
            variant="ghost"
            title="Back to Workflows"
            className="ml-2 mt-2"
            onClick={() => navigate("/admin/workflow-management")}>
            <ArrowLeft />
          </Button>
          <h2 className="text-lg font-semibold text-gray-700 mt-[14px]">Workflow Nodes</h2>
        </div>
        <div className="text-sm text-gray-500">Drag & drop to canvas and join with edges</div>

        {/* AI Assistant Button */}
        <Button
          onClick={() => setIsAIModalOpen(true)}
          className="w-full flex items-center justify-center gap-2 p-3 rounded-lg shadow-md"
        >
          <Sparkles className="h-4 w-4 animate-pulse" />
          <span className="font-medium">AI Assistant</span>
        </Button>
      </div>

      {/* Scrollable Node Lists */}
      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-6">
        {renderNodeList(
          "Triggers",
          triggers,
          isLoadingTriggers,
          <Zap size={20} className="text-blue-600 shrink-0" />,
          "bg-blue-50",
          "border-blue-200",
          "bg-blue-100",
          "text-blue-900"
        )}

        {renderNodeList(
          "Actions",
          actions,
          isLoadingActions,
          <MessageSquare size={20} className="text-purple-600 shrink-0" />,
          "bg-purple-50",
          "border-purple-200",
          "bg-purple-100",
          "text-purple-900"
        )}

        {renderNodeList(
          "Conditions",
          conditions,
          isLoadingConditions,
          <GitBranch size={20} className="text-orange-600 shrink-0" />,
          "bg-orange-50",
          "border-orange-200",
          "bg-orange-100",
          "text-orange-900"
        )}

        {/* renderNodeList(
          "Sub Workflows",
          triggerableWorkflows,
          isLoadingTriggerableWorkflows,
          <GitBranch size={20} className="text-green-600 shrink-0" />,
          "bg-green-50",
          "border-green-200",
          "bg-green-100",
          "text-green-900"
        ) */}
      </div>

      {/* Create Workflow Button */}
      <div className="p-4 border-t border-gray-200 bg-white">

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              disabled={isCreating}
              className="w-full bg-primary text-white px-4 py-2 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isCreating && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              {isCreating
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                  ? "Update Workflow"
                  : "Create Workflow"}
              <ChevronDown className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end">
            <DropdownMenuItem onClick={() => onCreateWorkflow()}>
              {isEditMode ? "Update Workflow" : "Create Workflow"}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onCreateWorkflow("active")}>
              {isEditMode ? "Update and Activate Workflow" : "Create and Activate Workflow"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <AIWorkflowModal
        isOpen={isAIModalOpen}
        onOpenChange={setIsAIModalOpen}
        onWorkflowGenerated={onAIWorkflowGenerated}
      />
    </aside>
  );
};
